import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import { copyToClipboard } from '../../utils/helpers';
import Notification from '../../utils/notification';
import styles from './InvitationSection.module.css';

const InvitationSection = () => {
  const [inviteData, setInviteData] = useState({
    inviteCode: '',
    successfulInvites: 0,
    totalRewards: 0
  });

  const [inviteHistory, setInviteHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchInviteData();
  }, []);

  const fetchInviteData = async () => {
    try {
      setLoading(true);

      // 获取用户资料（包含邀请码）
      const profileResponse = await userAPI.getProfile();
      const profileData = profileResponse.data.data;

      // 获取被邀请用户列表
      const response = await userAPI.getInvitedUsers();
      const data = response.data.data;

      // 处理邀请历史数据
      const inviteList = data.records || [];
      setInviteHistory(inviteList);

      // 计算统计信息
      const successfulInvites = inviteList.filter(invite => invite.status === 1).length;
      const totalRewards = inviteList.reduce((sum, invite) => sum + (invite.coinReward || 0), 0);

      setInviteData({
        inviteCode: profileData.inviteCode || '',
        successfulInvites,
        totalRewards
      });

    } catch (error) {
      console.error('Failed to fetch invite data:', error);
      Notification.error('获取邀请数据失败');
    } finally {
      setLoading(false);
    }
  };

  const getInviteLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite?code=${inviteData.inviteCode}`;
  };

  const handleCopyLink = async () => {
    const inviteLink = getInviteLink();
    const success = await copyToClipboard(inviteLink);

    if (success) {
      Notification.success('邀请链接已复制到剪贴板');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } else {
      Notification.error('复制失败，请手动复制链接');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN');
  };

  const getStatusColor = (status) => {
    // status: 0-无效，1-有效
    if (status === 1) return '#52c41a';
    if (status === 0) return '#999';
    return '#999';
  };

  const getStatusText = (status) => {
    // status: 0-无效，1-有效
    if (status === 1) return '已注册';
    if (status === 0) return '无效';
    return '未知';
  };

  if (loading) {
    return (
      <div className={styles.invitationSection}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.invitationSection}>
      <div className={styles.invitationHeader}>
        <h2>邀请管理</h2>
        <p>邀请好友注册获得丰厚奖励，被邀请用户也能获得注册奖励</p>
      </div>

      <div className={styles.invitationStats}>
        <div className={`${styles.statCard} ${styles.success}`}>
          <div className={styles.statIcon}>✅</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{inviteData.successfulInvites}</span>
            <span className={styles.statLabel}>成功邀请</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.rewards}`}>
          <div className={styles.statIcon}>💰</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{inviteData.totalRewards}</span>
            <span className={styles.statLabel}>累计奖励</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.inviteLink}`}>
          {/* <div className={styles.statIcon}>🔗</div> */}
          <div className={styles.statContent}>
            <span className={styles.statLabel}>邀请链接</span>
            <div className={styles.inviteLinkDisplay}>
              <input
                type="text"
                value={getInviteLink()}
                readOnly
                className={styles.inviteLinkInput}
              />
              <button
                className={styles.copyBtn}
                onClick={handleCopyLink}
                disabled={copySuccess}
              >
                {copySuccess ? '✅ 已复制' : '📋 复制'}
              </button>
            </div>
            <p className={styles.inviteTip}>
              分享此链接给好友，好友通过链接注册后您将获得 10 金币奖励
            </p>
          </div>
        </div>
      </div>

      <div className={styles.inviteHistory}>
        <h3>邀请记录</h3>
        {inviteHistory.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>👥</div>
            <h4>暂无邀请记录</h4>
            <p>开始邀请好友，赚取更多金币奖励</p>
          </div>
        ) : (
          <div className={styles.historyTable}>
            <div className={styles.tableHeader}>
              <span>邀请用户</span>
              <span>邀请时间</span>
              <span>注册时间</span>
              <span>状态</span>
              <span>奖励</span>
            </div>
            {inviteHistory.map(invite => (
              <div key={invite.id} className={styles.tableRow}>
                <span className={styles.inviteeInfo}>
                  <div className={styles.inviteeName}>{invite.nickname || invite.username || '匿名用户'}</div>
                  <div className={styles.inviteeEmail}>{invite.email || '-'}</div>
                </span>
                <span className={styles.inviteDate}>{formatDate(invite.invitedAt)}</span>
                <span className={styles.joinDate}>{formatDate(invite.invitedAt)}</span>
                <span
                  className={styles.inviteStatus}
                  style={{ color: getStatusColor(invite.status) }}
                >
                  {getStatusText(invite.status)}
                </span>
                <span className={styles.inviteReward}>
                  {invite.coinReward > 0 ? `+${invite.coinReward}` : '-'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationSection;